
<ng-template #loader>
  <terra-loader class="page-loader"></terra-loader>
</ng-template>

<ng-container *ngIf="application$ | async as application; else loader">
  <!-- Asset page header -->
  <div class="title-area container">
    <h1 class="lo_sensitive">{{ application.name | truncate: 45 }}</h1>
    <span class="status">
      <mat-icon class="notranslate">
        {{ getstatusIcon(application.status) }}
      </mat-icon>
      {{ application.status | enumString: ApplicationStatus }}
    </span>
    <div class="right-aligned">
      <a mat-stroked-button routerLink="edit" *ngIf="application.status === ApplicationStatus.Draft" class="edit-button"
         color="primary">
        <mat-icon class="notranslate">edit</mat-icon>
        Continue Editing
      </a>

      @if(application.status === ApplicationStatus.Draft || application.status !== ApplicationStatus.Approved && (isCovercyUser$ | async))
      {
      <button mat-icon-button color="primary"
              [matMenuTriggerFor]="moreActions" class="more-actions-toggle">
        <mat-icon class="notranslate">more_vert</mat-icon>
      </button>
      <mat-menu #moreActions="matMenu">
        <button mat-menu-item (click)="deleteDraft()"
                class="delete-button">
          <mat-icon class="notranslate">delete_forever</mat-icon>
          Delete Draft
        </button>
      </mat-menu>
      }

    </div>

  </div>
  <section class="details-section container">
    <!-- Application details -->
    <div class="terra-card">
      <div class="card-header">
        <h2>Application Details</h2>
      </div>
      <ul class="basic-info-list">
        <!-- Name of Business -->
        <li class="basic-info-item">
          <mat-icon class="notranslate">business</mat-icon>
          <div class="basic-info-item-content">
            <label>Name of Business:</label>
            <span class="value">{{application.name}}</span>
          </div>
        </li>
        <!-- Business Contact -->
        <li class="basic-info-item">
          <mat-icon class="notranslate">person</mat-icon>
          <div class="basic-info-item-content">
            <label>Business Contact:</label>
            <span class="value">{{application.contact.fullName.first}} {{application.contact.fullName.last}}</span>
          </div>
        </li>
        <!-- Application sent date -->
        <li class="basic-info-item">
          <mat-icon class="notranslate">date_range</mat-icon>
          <div class="basic-info-item-content">
            <label>Application sent:</label>
            <span class="value">{{application.createdAtUnit | date}}</span>
          </div>
        </li>
      </ul>
    </div>
    <!-- Officer details -->
    <div class="terra-card">
      <div class="card-header">
        <h2>Officer Details</h2>
      </div>
      <ul class="basic-info-list">
        <!-- Officer name -->
        <li class="basic-info-item">
          <mat-icon class="notranslate">person</mat-icon>
          <div class="basic-info-item-content">
            <label>Name:</label>
            <span class="value">{{application.officer.fullName.first}} {{application.officer.fullName.last}}</span>
          </div>
        </li>
        <!-- Title -->
        <li class="basic-info-item">
          <mat-icon class="notranslate">work</mat-icon>
          <div class="basic-info-item-content">
            <label>Title:</label>
            <span class="value">{{splitByUppercase(application.officer.title)}}</span>
          </div>
        </li>
        <!-- Officer Passport and nationality -->
        <li class="basic-info-item" *ngIf="application.officer.passport">
          <mat-icon class="notranslate">account_box</mat-icon>
          <div class="basic-info-item-content">
            <label>Passport:</label>
            <span class="value">{{application.officer.passport}} ({{application.officer.nationality}})</span>
          </div>
        </li>
      </ul>
    </div>
    <!-- Beneficial owners -->
    <div class="terra-card">
      <div class="card-header">
        <h2>Beneficial Owners</h2>
      </div>
      <ul class="beneficial-owners-list basic-info-list">
        <!-- Beneficial owners list -->
        <li *ngFor="let b of application.beneficialOwners" class="basic-info-item beneficial-owner">
          <div class="basic-info-item-content">
            <label class="truncate-to-width">{{b.fullName.first}} {{b.fullName.last}}</label>
            <span class="value">{{b.percentage | terraNumber }}%</span>
          </div>
        </li>
        <span class="empty-message" *ngIf="application.beneficialOwners.length === 0">No beneficial owners
          added</span>
      </ul>
    </div>
  </section>

  <!-- Documents section -->
  <section class="documents-section container"
    *ngIf="(documents$ | async)?.length && application.status === ApplicationStatus.AwaitingDocuments">
    <div class="terra-card">
      <div class="card-header">
        <h2 class="attention documents-header">Documents required</h2>
      </div>
      <div class="card-content">
        <p class="attention action-description">
          <ng-container *ngIf="!(allDocumentsUploaded$ | async)">
            Please upload the following documents.
          </ng-container>
          <ng-container *ngIf="(allDocumentsUploaded$ | async)">
            Successfully uploaded all documents.
          </ng-container>
        </p>

        <p class="supported-files-description" *ngIf="!(allDocumentsUploaded$ | async)">
          File format must be JPG, JPEG, PNG (20 MB max file size) or PDF (50 MB max file size)
        </p>

        <form [formGroup]="documentsForm" class="documents-form">
          <ul class="documents-list" formArrayName="documents">
            <li *ngFor="let document of application.documents; index as i" [formGroupName]="i"
              [ngClass]="{'doc-pending-review': !(document.status === UnitDocumentStatus.Required || document.status === UnitDocumentStatus.Invalid)}">
              <span class="document-title truncate-to-width">
                {{ document.documentType | enumString : UnitDocumentType}} - {{ document.name }}
                <span class="attention">(Required)</span>
              </span>

              <!-- File Uploader -->
              <terra-file-uploader formControlName="metaFileLink" [supportedExtensions]="documentSupportedExtensions"
                *ngIf="document.status === UnitDocumentStatus.Required || document.status === UnitDocumentStatus.Invalid"
                class="file-uploader" no-type-select></terra-file-uploader>

              <!-- File Uploader - Back side - ID document only -->
              <terra-file-uploader formControlName="backSideMetaFileLink"
                [supportedExtensions]="documentSupportedExtensions"
                *ngIf="document.documentType == UnitDocumentType.IdDocument
                     && (document.status === UnitDocumentStatus.Required || document.status === UnitDocumentStatus.Invalid)" class="file-uploader-back"
                uploadButtonText="+ Upload Back Side" no-type-select></terra-file-uploader>

              <!-- Document status -->
              <span *ngIf="document.status !== UnitDocumentStatus.Required" class="document-status">
                <mat-icon class="notranslate">{{getDocumentStatusIcon(document.status)}}</mat-icon>
                {{document.status | enumString : UnitDocumentStatus}}</span>

              <span class="fail-reason" *ngIf="document.status === UnitDocumentStatus.Invalid">
                Reason: {{document.reasonCode | enumString : UnitDocumentReasonCode}}
              </span>
              <mat-icon *ngIf="document?.reason" class="notranslate tooltip-icon" color="primary"
                matTooltipClass="terra-tooltip" matTooltipPosition="right" matTooltip="{{document.reason}}">
                help
              </mat-icon>
            </li>
          </ul>
        </form>
      </div>
    </div>
  </section>
</ng-container>
